import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { auth } from '../config/firebase'
import { updateChild } from '../services/parentService'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2, Save, ArrowLeft, Shuffle } from 'lucide-react'
import { useChild } from '@/context/ChildContext'
import { toast } from 'sonner'
import { createAvatar } from '@dicebear/core'
import { adventurer, bottts, funEmoji, lorelei, pixelArt, thumbs } from '@dicebear/collection'

// Style configurations with their customization options
const AVATAR_STYLES = {
  adventurer: {
    name: 'Adventurer',
    description: 'Cute explorer characters',
    style: adventurer,
    options: {
      backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf', 'f4d9b2', 'c9e4ca', 'f9e7c0'],
      skinColor: ['f2d3b1', 'ecad80', 'd78f5a', 'c67b48', 'a56c43', '8d5524', '765339', '613d24'],
      hair: ['short01', 'short02', 'short03', 'short04', 'short05', 'long01', 'long02', 'long03', 'long04', 'long05', 'long06', 'long07', 'long08', 'long09', 'long10', 'long11', 'long12', 'long13', 'long14', 'long15', 'long16', 'long17', 'long18', 'long19', 'long20', 'long21', 'long22', 'long23', 'long24', 'long25', 'long26'],
      hairColor: ['0e0e0e', '3a3a3a', '6a4e35', '977961', 'b58143', 'cb6820', 'dba520', 'e6be8a', 'ecc283', 'ab2a1c', 'c13823', 'd6694a', '6c4545', '2c1b18', '724133', 'a55728'],
      eyes: ['variant01', 'variant02', 'variant03', 'variant04', 'variant05', 'variant06', 'variant07', 'variant08', 'variant09', 'variant10', 'variant11', 'variant12', 'variant13', 'variant14', 'variant15', 'variant16', 'variant17', 'variant18', 'variant19', 'variant20', 'variant21', 'variant22', 'variant23', 'variant24', 'variant25', 'variant26'],
      eyebrows: ['variant01', 'variant02', 'variant03', 'variant04', 'variant05', 'variant06', 'variant07', 'variant08', 'variant09', 'variant10', 'variant11', 'variant12', 'variant13', 'variant14', 'variant15'],
      mouth: ['variant01', 'variant02', 'variant03', 'variant04', 'variant05', 'variant06', 'variant07', 'variant08', 'variant09', 'variant10', 'variant11', 'variant12', 'variant13', 'variant14', 'variant15', 'variant16', 'variant17', 'variant18', 'variant19', 'variant20', 'variant21', 'variant22', 'variant23', 'variant24', 'variant25', 'variant26', 'variant27', 'variant28', 'variant29', 'variant30'],
      glasses: ['variant01', 'variant02', 'variant03', 'variant04', 'variant05'],
      glassesProbability: [0, 100],
    }
  },
  lorelei: {
    name: 'Portraits',
    description: 'Beautiful face portraits',
    style: lorelei,
    options: {
      backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf', 'f4d9b2', 'c9e4ca', 'f9e7c0'],
      skinColor: ['f2d3b1', 'ecad80', 'd78f5a', 'c67b48', 'a56c43', '8d5524'],
      hair: ['variant01', 'variant02', 'variant03', 'variant04', 'variant05', 'variant06', 'variant07', 'variant08', 'variant09', 'variant10', 'variant11', 'variant12', 'variant13', 'variant14', 'variant15', 'variant16', 'variant17', 'variant18', 'variant19', 'variant20', 'variant21', 'variant22', 'variant23', 'variant24', 'variant25', 'variant26', 'variant27', 'variant28', 'variant29', 'variant30', 'variant31', 'variant32', 'variant33', 'variant34', 'variant35', 'variant36', 'variant37', 'variant38', 'variant39', 'variant40', 'variant41', 'variant42', 'variant43', 'variant44', 'variant45'],
      hairColor: ['0e0e0e', '3a3a3a', '6a4e35', '977961', 'b58143', 'dba520', 'ab2a1c', 'c13823'],
      eyes: ['variant01', 'variant02', 'variant03', 'variant04', 'variant05', 'variant06', 'variant07', 'variant08', 'variant09', 'variant10', 'variant11', 'variant12', 'variant13', 'variant14', 'variant15', 'variant16', 'variant17', 'variant18', 'variant19', 'variant20', 'variant21', 'variant22', 'variant23', 'variant24'],
      eyebrows: ['variant01', 'variant02', 'variant03', 'variant04', 'variant05', 'variant06', 'variant07', 'variant08', 'variant09', 'variant10', 'variant11', 'variant12', 'variant13'],
      mouth: ['happy01', 'happy02', 'happy03', 'happy04', 'happy05', 'happy06', 'happy07', 'happy08', 'happy09', 'happy10', 'happy11', 'happy12', 'happy13', 'happy14', 'happy15', 'happy16', 'sad01', 'sad02', 'sad03', 'sad04', 'sad05', 'sad06', 'sad07', 'sad08'],
      glasses: ['variant01', 'variant02', 'variant03', 'variant04', 'variant05'],
      glassesProbability: [0, 100],
    }
  },
  bottts: {
    name: 'Robots',
    description: 'Fun robot friends',
    style: bottts,
    options: {
      backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf', 'f4d9b2', 'c9e4ca', 'f9e7c0'],
      baseColor: ['00acc1', '1e88e5', '5e35b1', '8e24aa', 'd81b60', 'e53935', 'f4511e', 'fb8c00', 'fdd835', '43a047', '00897b', '3949ab', '546e7a', '757575', '6d4c41'],
      eyes: ['bulging', 'dizzy', 'eva', 'frame1', 'frame2', 'glow', 'happy', 'hearts', 'robocop', 'round', 'roundFrame01', 'roundFrame02', 'sensor', 'shade01'],
      face: ['square01', 'square02', 'square03', 'square04', 'round01', 'round02'],
      mouth: ['bite', 'diagram', 'grill01', 'grill02', 'grill03', 'smile01', 'smile02', 'square01', 'square02'],
      sides: ['antenna01', 'antenna02', 'cables01', 'cables02', 'round', 'square', 'squareAssymetric'],
      top: ['antenna', 'antennaCrooked', 'bulb01', 'glowingBulb01', 'glowingBulb02', 'horns', 'lights', 'radar', 'pyramidSpike'],
    }
  },
  pixelArt: {
    name: 'Pixel Art',
    description: 'Retro pixel style',
    style: pixelArt,
    options: {
      backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf', 'f4d9b2', 'c9e4ca', 'f9e7c0'],
      skinColor: ['ecad80', 'f2d3b1', 'd78f5a', 'a56c43', '8d5524'],
      hair: ['short01', 'short02', 'short03', 'short04', 'short05', 'long01', 'long02', 'long03', 'long04', 'long05'],
      hairColor: ['0e0e0e', '6a4e35', 'b58143', 'dba520', 'ab2a1c', '28150a'],
      eyes: ['variant01', 'variant02', 'variant03', 'variant04', 'variant05', 'variant06', 'variant07', 'variant08', 'variant09', 'variant10', 'variant11', 'variant12'],
      eyebrows: ['variant01', 'variant02', 'variant03', 'variant04', 'variant05', 'variant06', 'variant07', 'variant08', 'variant09', 'variant10', 'variant11', 'variant12', 'variant13'],
      mouth: ['happy01', 'happy02', 'happy03', 'happy04', 'happy05', 'happy06', 'happy07', 'happy08', 'happy09', 'happy10', 'happy11', 'happy12', 'sad01', 'sad02', 'sad03', 'sad04', 'sad05', 'sad06', 'sad07', 'sad08'],
      glasses: ['variant01', 'variant02', 'variant03', 'variant04', 'variant05'],
      glassesProbability: [0, 100],
      accessories: ['variant01', 'variant02', 'variant03', 'variant04'],
      accessoriesProbability: [0, 100],
    }
  },
  funEmoji: {
    name: 'Emoji',
    description: 'Expressive emojis',
    style: funEmoji,
    options: {
      backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf', 'f4d9b2', 'c9e4ca', 'f9e7c0', 'fcee4e', 'a7e75e'],
      eyes: ['closed', 'closed2', 'crying', 'cute', 'glasses', 'love', 'pissed', 'plain', 'sad', 'shades', 'sleepClose', 'stars', 'tearDrop', 'wink', 'wink2'],
      mouth: ['cute', 'drip', 'faceMask', 'kissHeart', 'lilSmile', 'pissed', 'plain', 'sad', 'shout', 'shy', 'sick', 'smileLol', 'smileTeeth', 'tongueOut', 'wideSmile'],
    }
  },
  thumbs: {
    name: 'Thumbs',
    description: 'Thumbs up!',
    style: thumbs,
    options: {
      backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf', 'f4d9b2', 'c9e4ca', 'f9e7c0'],
      shapeColor: ['0a5b83', '1c799f', '69d2e7', 'f38b74', 'e23d41', 'fc909f', 'a55eea', 'e8d5b7', 'f69256', 'f4d06f'],
      eyes: ['variant1W12', 'variant1W14', 'variant1W16', 'variant2W10', 'variant2W12', 'variant2W14', 'variant2W16', 'variant3W10', 'variant3W12', 'variant3W14', 'variant3W16', 'variant4W10', 'variant4W12', 'variant4W14', 'variant4W16', 'variant5W10', 'variant5W12', 'variant5W14', 'variant5W16', 'variant6W10', 'variant6W12', 'variant6W14', 'variant6W16', 'variant7W10', 'variant7W12', 'variant7W14', 'variant7W16', 'variant8W10', 'variant8W12', 'variant8W14', 'variant8W16', 'variant9W10', 'variant9W12', 'variant9W14', 'variant9W16'],
      mouth: ['variant1', 'variant2', 'variant3', 'variant4', 'variant5'],
    }
  },
} as const

type AvatarStyleId = keyof typeof AVATAR_STYLES

// Avatar config type
interface AvatarConfig {
  style: AvatarStyleId
  seed: string
  options: Record<string, string | number>
}

// Generate a random seed
function generateSeed(): string {
  return Math.random().toString(36).substring(2, 10)
}

// Pick random item from array
function pickRandom<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]
}

// Generate random options for a style
function generateRandomOptions(styleId: AvatarStyleId): Record<string, string | number> {
  const styleConfig = AVATAR_STYLES[styleId]
  const options: Record<string, string | number> = {}
  
  for (const [key, values] of Object.entries(styleConfig.options)) {
    if (key.endsWith('Probability')) {
      options[key] = Math.random() > 0.7 ? 100 : 0
    } else if (Array.isArray(values)) {
      options[key] = pickRandom(values)
    }
  }
  
  return options
}

// Parse saved avatar config
function parseAvatarConfig(saved: string | undefined): AvatarConfig {
  const defaultStyle: AvatarStyleId = 'adventurer'
  if (!saved) {
    return { 
      style: defaultStyle, 
      seed: generateSeed(),
      options: generateRandomOptions(defaultStyle)
    }
  }
  try {
    const parsed = JSON.parse(saved)
    const style = (parsed.style in AVATAR_STYLES ? parsed.style : defaultStyle) as AvatarStyleId
    return {
      style,
      seed: parsed.seed || generateSeed(),
      options: parsed.options || generateRandomOptions(style)
    }
  } catch {
    return { 
      style: defaultStyle, 
      seed: generateSeed(),
      options: generateRandomOptions(defaultStyle)
    }
  }
}

// Generate avatar data URI
function generateAvatarUri(config: AvatarConfig, size = 128): string {
  const styleConfig = AVATAR_STYLES[config.style]
  if (!styleConfig) return ''
  
  // DiceBear expects array options - wrap single values in arrays
  const dicebearOptions: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(config.options)) {
    if (key.endsWith('Probability')) {
      // Probability values stay as numbers
      dicebearOptions[key] = value
    } else if (typeof value === 'string') {
      // Wrap string values in arrays for DiceBear
      dicebearOptions[key] = [value]
    } else {
      dicebearOptions[key] = value
    }
  }
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return createAvatar(styleConfig.style as any, {
    seed: config.seed,
    size,
    ...dicebearOptions,
  }).toDataUri()
}

// Friendly names for option keys
const OPTION_LABELS: Record<string, string> = {
  backgroundColor: 'Background',
  skinColor: 'Skin',
  hair: 'Hair Style',
  hairColor: 'Hair Color',
  eyes: 'Eyes',
  eyebrows: 'Eyebrows',
  mouth: 'Mouth',
  glasses: 'Glasses',
  glassesProbability: 'Show Glasses',
  baseColor: 'Robot Color',
  face: 'Face Shape',
  sides: 'Side Parts',
  top: 'Head Top',
  shapeColor: 'Color',
  accessories: 'Accessories',
  accessoriesProbability: 'Show Accessories',
}

export default function ChildProfilePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const childId = searchParams.get('childId')
  const user = auth.currentUser
  
  const { childrenProfiles, refreshChildren } = useChild()
  const child = childrenProfiles.find(c => c.id === childId)
  
  // Form state
  const [displayName, setDisplayName] = useState('')
  const [config, setConfig] = useState<AvatarConfig | null>(null)
  const [saving, setSaving] = useState(false)
  const [initialized, setInitialized] = useState(false)

  // Redirect if not logged in or no childId
  useEffect(() => {
    if (!user) {
      navigate('/auth')
    } else if (!childId) {
      navigate('/parent-dashboard')
    }
  }, [user, childId, navigate])

  // Initialize form when child data is available (only once)
  useEffect(() => {
    if (child && !initialized) {
      setDisplayName(child.displayName)
      setConfig(parseAvatarConfig(child.avatarConfig))
      setInitialized(true)
    }
  }, [child, initialized])

  // Generate the current avatar
  const currentAvatarUri = useMemo(() => {
    if (!config) return ''
    return generateAvatarUri(config, 200)
  }, [config])

  // Change style
  const handleStyleChange = (styleId: AvatarStyleId) => {
    setConfig({
      style: styleId,
      seed: generateSeed(),
      options: generateRandomOptions(styleId)
    })
  }

  // Update single option
  const handleOptionChange = (key: string, value: string | number) => {
    if (!config) return
    setConfig({
      ...config,
      options: { ...config.options, [key]: value }
    })
  }

  // Generate new random seed and options
  const handleRandomize = () => {
    if (!config) return
    setConfig({
      ...config,
      seed: generateSeed(),
      options: generateRandomOptions(config.style)
    })
  }

  // Save to Firebase
  const handleSave = async () => {
    if (!user || !childId || !displayName.trim() || !config) return
    
    setSaving(true)
    try {
      await updateChild(user.uid, childId, {
        displayName: displayName.trim(),
        avatarConfig: JSON.stringify(config)
      })
      
      await refreshChildren()
      toast.success('Profile saved!')
      navigate('/parent-dashboard')
    } catch (error) {
      console.error('Failed to save:', error)
      toast.error('Failed to save profile')
    } finally {
      setSaving(false)
    }
  }

  // Loading state
  if (!child || !config) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-medium-teal" />
      </div>
    )
  }

  const currentStyleConfig = AVATAR_STYLES[config.style]

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/parent-dashboard')}
          className="text-dark-navy hover:bg-white/20"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-dark-navy">Edit Profile</h1>
          <p className="text-dark-navy/60">Customize {child.displayName}'s avatar</p>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-2 lg:grid-cols-[320px,1fr] gap-6">
        
        {/* LEFT COLUMN - Avatar Preview (Sticky) */}
        <div className="lg:sticky lg:top-24 lg:self-start space-y-4">
          

          {/* Name Input */}
          <Card className="bg-white/90 backdrop-blur-sm shadow-lg">
            <CardContent className="p-4">
              <Label htmlFor="name" className="text-dark-navy font-medium">Display Name</Label>
              <Input
                id="name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter name"
                className="mt-2"
              />
            </CardContent>
          </Card>

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={saving || !displayName.trim()}
            size="lg"
            className="w-full bg-medium-purple hover:bg-deep-purple text-white shadow-lg"
          >
            {saving ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</>
            ) : (
              <><Save className="mr-2 h-4 w-4" /> Save Profile</>
            )}
          </Button>

          <Card className="bg-white/90 backdrop-blur-sm shadow-xl">
            <CardContent className="p-6 flex flex-col items-center gap-4">
              <div className="w-48 h-48 rounded-full border-4 border-white shadow-xl overflow-hidden bg-gradient-to-br from-light-teal/20 to-medium-purple/20">
                <img 
                  src={currentAvatarUri} 
                  alt="Avatar preview" 
                  className="w-full h-full object-cover"
                />
              </div>
              
              <Button variant="outline" onClick={handleRandomize} className="w-full" size="lg">
                <Shuffle className="h-4 w-4 mr-2" />
                Randomize All
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* RIGHT COLUMN - Customization Options */}
        <div className="space-y-4">
          
          {/* Style Selection */}
          <Card className="bg-white/90 backdrop-blur-sm shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Avatar Style</CardTitle>
              <CardDescription>Choose your character type</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {Object.entries(AVATAR_STYLES).map(([id, styleOption]) => {
                  const previewConfig: AvatarConfig = { 
                    style: id as AvatarStyleId, 
                    seed: 'preview',
                    options: {}
                  }
                  const previewUri = generateAvatarUri(previewConfig, 64)
                  const isSelected = config.style === id
                  
                  return (
                    <button
                      key={id}
                      onClick={() => handleStyleChange(id as AvatarStyleId)}
                      className={`
                        p-2 rounded-xl border-2 transition-all hover:scale-105
                        ${isSelected 
                          ? 'border-medium-teal bg-medium-teal/10 shadow-md' 
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                        }
                      `}
                    >
                      <div className="w-12 h-12 mx-auto rounded-full overflow-hidden bg-gray-50">
                        <img src={previewUri} alt={styleOption.name} className="w-full h-full object-cover" />
                      </div>
                      <p className={`text-xs font-medium mt-1 text-center truncate ${isSelected ? 'text-medium-teal' : 'text-dark-navy'}`}>
                        {styleOption.name}
                      </p>
                    </button>
                  )
                })}
              </div>
            </CardContent>
          </Card>

          {/* Feature Customization */}
          <Card className="bg-white/90 backdrop-blur-sm shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Customize Features</CardTitle>
              <CardDescription>Fine-tune your {currentStyleConfig.name} avatar</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {Object.entries(currentStyleConfig.options).map(([optionKey, values]) => {
                const label = OPTION_LABELS[optionKey] || optionKey
                const currentValue = config.options[optionKey]
                
                // Handle probability toggles
                if (optionKey.endsWith('Probability')) {
                  return (
                    <div key={optionKey} className="flex items-center justify-between">
                      <Label className="text-sm font-medium text-dark-navy">{label}</Label>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={currentValue === 0 ? 'default' : 'outline'}
                          onClick={() => handleOptionChange(optionKey, 0)}
                          className={currentValue === 0 ? 'bg-medium-teal hover:bg-light-teal' : ''}
                        >
                          Off
                        </Button>
                        <Button
                          size="sm"
                          variant={currentValue === 100 ? 'default' : 'outline'}
                          onClick={() => handleOptionChange(optionKey, 100)}
                          className={currentValue === 100 ? 'bg-medium-teal hover:bg-light-teal' : ''}
                        >
                          On
                        </Button>
                      </div>
                    </div>
                  )
                }
                
                // Handle color options
                if (optionKey.toLowerCase().includes('color') || optionKey === 'backgroundColor') {
                  return (
                    <div key={optionKey}>
                      <Label className="text-sm font-medium text-dark-navy mb-2 block">{label}</Label>
                      <div className="flex flex-wrap gap-2">
                        {(values as readonly string[]).map((color) => (
                          <button
                            key={color}
                            onClick={() => handleOptionChange(optionKey, color)}
                            className={`
                              w-8 h-8 rounded-full border-2 transition-all hover:scale-110
                              ${currentValue === color ? 'border-dark-navy ring-2 ring-medium-teal ring-offset-2' : 'border-gray-300'}
                            `}
                            style={{ backgroundColor: `#${color}` }}
                            title={`#${color}`}
                          />
                        ))}
                      </div>
                    </div>
                  )
                }
                
                // Handle other options (variants)
                return (
                  <div key={optionKey}>
                    <Label className="text-sm font-medium text-dark-navy mb-2 block">{label}</Label>
                    <div className="flex flex-wrap gap-2">
                      {(values as readonly string[]).slice(0, 12).map((variant, idx) => (
                        <button
                          key={variant}
                          onClick={() => handleOptionChange(optionKey, variant)}
                          className={`
                            px-3 py-1.5 text-xs rounded-lg border transition-all
                            ${currentValue === variant 
                              ? 'bg-medium-teal text-white border-medium-teal' 
                              : 'bg-white text-dark-navy border-gray-200 hover:border-medium-teal'
                            }
                          `}
                        >
                          {idx + 1}
                        </button>
                      ))}
                      {(values as readonly string[]).length > 12 && (
                        <span className="px-3 py-1.5 text-xs text-gray-500">
                          +{(values as readonly string[]).length - 12} more
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}










